/**
 * Unités, de la plus grosse à la plus fine.
 *
 * Ni semaine ni trimestre : dans une file de modération, ce qui compte est le
 * nombre de **jours** écoulés — la directive App Store 1.2 se compte en heures,
 * « il y a 2 semaines » demanderait une conversion mentale que « il y a 13
 * jours » évite.
 *
 * Le mois vaut 30 jours et l'année 365 : l'approximation est invisible à
 * l'échelle où ces libellés servent, et éviterait de toute façon mal les
 * années bissextiles.
 */
const UNITS = [
  ["year", 365 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["minute", 60 * 1000],
  ["second", 1000],
] as const satisfies readonly (readonly [
  Intl.RelativeTimeFormatUnit,
  number,
])[];

/**
 * « il y a 3 jours », « 3 days ago » — l'âge d'une date, dans la langue voulue.
 *
 * Fonction **pure** : l'instant de référence est un argument, jamais
 * `Date.now()`. C'est ce qui la rend testable sans figer l'horloge, et ce qui
 * empêche deux appels d'un même rendu de tomber sur des secondes différentes.
 *
 * `numeric: "auto"` fait dire « hier » plutôt que « il y a 1 jour » : c'est
 * `Intl` qui connaît ces formes, langue par langue, et il y en a plus qu'on ne
 * croit.
 *
 * @param iso - date ISO venue du serveur.
 * @param locale - langue courante.
 * @param now - instant de référence, en millisecondes.
 * @returns le libellé, ou `null` si la date est illisible — à l'appelant de
 *   décider quoi afficher plutôt que de laisser passer un « Invalid Date ».
 */
export function relativeTime(
  iso: string,
  locale: string,
  now: number,
): string | null {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;

  const elapsed = then - now;
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  for (const [unit, size] of UNITS) {
    if (Math.abs(elapsed) >= size) {
      return format.format(Math.round(elapsed / size), unit);
    }
  }

  // Moins d'une seconde : « now » / « maintenant ».
  return format.format(0, "second");
}
